import { Inject, Injectable } from '@angular/core';
import { AffiliateService } from '../services/affiliate.service';
import { BehaviorSubject, Observable, ReplaySubject, skip } from 'rxjs';
import { ICalculatePreview } from '../../interfaces/calculate-preview.interface';
import { IDetails, ILoanProduct, IPromoCode } from '../../models/models';
import * as moment from 'moment';
import { ActivatedRoute, Params } from '@angular/router';
import { take, tap } from 'rxjs/operators';
import { IScheduleEvent } from '../../interfaces/schedule-event';
import { LoanService } from '../../services/loan.service';
import { EnvironmentType } from '../../models/environment.type';
import { ENVIRONMENT, SESSION_STORAGE } from '@libs/shared/injections/window';
import { getCommissionAmount } from '@libs/helpers/helper.utils';
import { MOCK_PRODUCT_TOKEN, MockProduct } from '@libs/configs/calculate.token';
import { dateFormatUI } from '@libs/configs/regular-expression-config';
import { XirrService } from '@libs/shared/components/calculate/xirr.service';

interface IExpectedPayment {
  date: moment.Moment;
  amount: number;
  interest: number;
}

@Injectable()
export class CalculateStore {
  private product!: ILoanProduct | MockProduct;
  private preview: BehaviorSubject<ICalculatePreview | null> = new BehaviorSubject<ICalculatePreview | null>(null);
  private schedule: ReplaySubject<Array<IScheduleEvent>> = new ReplaySubject<Array<IScheduleEvent>>();
  private isPreview: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private totalAmountSource: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  private readonly defaultKeys: string[] = ['amount', 'term', 'totalAmount', 'promoCode'];
  private time: number = moment().toDate().getTime();

  constructor(
    @Inject(MOCK_PRODUCT_TOKEN) private readonly mockProduct: MockProduct,
    @Inject(ENVIRONMENT) private environment: EnvironmentType,
    @Inject(SESSION_STORAGE) private sessionStorage: Storage,
    private affiliateService: AffiliateService,
    private activatedRoute: ActivatedRoute,
    private loanService: LoanService,
    private readonly xirrService: XirrService,
  ) {
    this.initQueryParamsSubscription();
  }

  get preview$(): Observable<ICalculatePreview> {
    return this.preview.asObservable();
  }

  get isPreviewVisible(): Observable<boolean> {
    return this.isPreview.asObservable();
  }

  set showPreview(value: boolean) {
    this.isPreview.next(value);
  }

  get schedule$(): Observable<Array<IScheduleEvent>> {
    return this.schedule.asObservable();
  }

  get getTotalAmountValue(): number {
    return this.totalAmountSource.getValue();
  }

  private static getExpectedPaymentAmount(loanAmount: number, interest: number, period: number): number {
    return Number(((loanAmount * interest * period) / 100).toFixed(2));
  }

  public setProduct(product: ILoanProduct | MockProduct): void {
    this.product = product;
  }

  public getProduct(): ILoanProduct | MockProduct {
    return this.product || this.mockProduct;
  }

  public init(product: ILoanProduct, showParams?: boolean): void {
    this.setProduct(product);
    this.affiliateService.setAffiliate();
    if (this.sessionStorage.getItem('promoCode') && !product.promoRate) {
      this.loanService
        .usePromoCode(this.sessionStorage.getItem('promoCode'))
        .pipe(
          tap((resp: IPromoCode) => {
            if (resp.actual) {
              this.setProduct({
                ...product,
                brandRate: product.interest,
                interest: resp.interest,
              });
              this.setDefaultParam(product, showParams);
            }
          }),
          take(1),
        )
        .subscribe();
    } else {
      this.setProduct({
        ...product,
        brandRate: product.interest,
        interest: product.promoRate || product.interest,
      });
      this.setDefaultParam(product, showParams);
    }
  }

  public setDefaultParam(product: ILoanProduct, showParams?: boolean): void {
    const amount = this.amount(product);
    const term = this.term(product);
    this.updatePreviewParam(amount, term, this.getProduct().interest, showParams);
  }

  public updateFakeSchedule(amount: number, term: number, interest: number): void {
    const data: IExpectedPayment[] = this.fill360ProductData(amount, interest, term);
    const scheduleData: IScheduleEvent[] = this.transformToScheduleData(data, dateFormatUI);
    scheduleData[0].amount = this.calculatePercentAmount(amount, term, interest);
    this.schedule.next(scheduleData);
  }

  public updateFakePreview(amount: number, term: number, interest: number) {
    const commission = getCommissionAmount(amount, interest, this.getProduct().issueCommissionRate);
    const data = {
      term,
      amount,
      percentByTerm: this.calculatePercentAmount(amount, term, interest),
      percentAmount: this.calculatePercentAmount(amount, this.getProduct().fullTerm, interest) + commission,
      totalAmount: amount + this.calculatePercentAmount(amount, this.getProduct().fullTerm, interest) + commission,
      returnDate: this.calculateReturnDate(term),
      interest,
      details: this.updateDetails(amount, term, interest),
      actionAmount: Number(((term * (this.getProduct() as MockProduct).actionRate * amount) / 100).toFixed(2)),
      firstPaymentPeriodAmount: this.calculatePercentAmount(amount, term, interest) + commission,
    };
    this.totalAmountSource.next(data.totalAmount);
    this.preview.next(data);
  }

  public updateDetails(startAmount: number, startTerm: number, interest: number): IDetails {
    const amount: number = startAmount;
    const term: number = startTerm;
    const percentAmount: number = (amount * term * interest) / 100;
    const realPercentPerYear: number = +this.xirrService.getXirr(this.environment.name, term).toFixed(2);
    return {
      percent: interest,
      percentPerYear: interest * 365,
      termLoan: 360,
      term,
      returnDate: this.calculateReturnDate(term),
      firstPayment: {
        dataToPay: this.calculateReturnDate(term),
        percentAmount,
        realPercentPerYear,
        totalAmount: percentAmount,
        totalCost: percentAmount + amount,
      },
    };
  }

  public updatePreviewParam(amount: number, term: number, interest: number, showParams: boolean = false): void {
    const data: ICalculatePreview = {
      term,
      amount,
      interest,
      percentAmount: this.calculatePercentAmount(amount, term, this.getProduct().interest),
      totalAmount: this.calculatePercentAmount(amount, term, interest),
      fullAmount: this.calculatePercentAmount(amount, term, this.getProduct().brandRate),
      returnDate: this.calculateReturnDate(term),
      productRate: this.getProduct().brandRate,
    };
    this.preview.next(data);
    this.isPreview.next(showParams);
  }

  public updateSchedule(
    amount: number,
    period: number,
    interest: number,
    is360: boolean,
    firstPeriodInterest: number,
    promoRate?: number,
  ): void {
    const data: IExpectedPayment[] = is360
      ? this.fill360ProductData(amount, interest, period)
      : this.fillNot360ProductData(amount, interest, period);

    const commission = getCommissionAmount(amount, interest, this.getProduct().issueCommissionRate, promoRate);

    this.updateFirsPaymentInterest(promoRate, data, amount, period, firstPeriodInterest, commission);

    const scheduleData: IScheduleEvent[] = this.transformToScheduleData(data, dateFormatUI);
    this.schedule.next(scheduleData);
  }

  public term(product: ILoanProduct | MockProduct): number {
    const storedTerm = Number(this.sessionStorage.getItem('term'));
    return this.getValidValue(storedTerm, product.startTerm, product.minTerm, product.maxTerm, (value) => value % 5 === 0);
  }

  public amount(product: ILoanProduct | MockProduct): number {
    const storedAmount = Number(this.sessionStorage.getItem('amount'));
    return this.getValidValue(storedAmount, product.startAmount, product.minAmount, product.maxAmount);
  }

  private calculatePercentAmount(amount: number, term: number, interest: number): number {
    return Number(((amount * interest * term) / 100).toFixed(2));
  }

  private calculateReturnDate(term: number): string {
    return moment()
      .add(term - 1, 'days')
      .format(dateFormatUI);
  }

  private getValidValue(
    storedValue: number | null,
    startValue: number,
    minValue: number,
    maxValue: number,
    validate?: (value: number) => boolean,
  ): number {
    if (storedValue && storedValue <= maxValue && storedValue >= minValue && (!validate || validate(storedValue))) {
      return storedValue;
    }
    if (startValue && startValue <= maxValue && startValue >= minValue) {
      return startValue;
    }
    return maxValue;
  }

  private fill360ProductData(amount: number, interest: number, period: number): IExpectedPayment[] {
    const endDate: moment.Moment = this.getLastPaymentDate();
    const data: IExpectedPayment[] = [];
    let next = this.buildInitial(amount, period, interest);
    while (next.date.isBefore(endDate)) {
      if (next.date.format(dateFormatUI) === endDate.format(dateFormatUI)) {
        break;
      }
      data.push(next);
      next = this.buildNext(next, period);
    }
    this.updateLastPaymentData(next, period, amount, interest);
    data.push(next);
    return data;
  }

  private buildInitial(amount: number, period: number, interest: number): IExpectedPayment {
    return {
      date: moment(this.time).add(period - 1, 'days'),
      amount: CalculateStore.getExpectedPaymentAmount(amount, interest, period),
      interest,
    };
  }

  private updateLastPaymentData(last: IExpectedPayment, period: number, amount: number, interest: number) {
    const lastPaymentDate: moment.Moment = this.getLastPaymentDate();
    const lastDateStartOfDay: moment.Moment = last.date.startOf('day');
    const lastPaymentDateStartOfDay: moment.Moment = lastPaymentDate.startOf('day');

    const daysDiff: number = lastDateStartOfDay.diff(lastPaymentDateStartOfDay, 'days');
    const finalPeriod: number = period - daysDiff;

    last.date = lastPaymentDate;
    last.amount = CalculateStore.getExpectedPaymentAmount(amount, interest, finalPeriod) + amount;
    last.interest = interest;
  }

  private getLastPaymentDate(): moment.Moment {
    const totalPeriod = this.getProduct().fullTerm || 360;
    return moment(this.time).add(totalPeriod - 1, 'days');
  }

  private fillNot360ProductData(amount: number, interest: number, period: number): IExpectedPayment[] {
    return [this.buildInitial(amount, period, interest)];
  }

  private buildNext(next: IExpectedPayment, period: number): IExpectedPayment {
    return {
      date: next.date.clone().add(period, 'days'),
      amount: next.amount,
      interest: next.interest,
    };
  }

  private updateFirsPaymentInterest(
    promoRate: number,
    data: any[],
    amount: number,
    period: number,
    firstPeriodInterest: number,
    commission: number,
  ) {
    const firstPeriodRate = Boolean(promoRate) && promoRate < firstPeriodInterest ? promoRate : firstPeriodInterest;
    data[0].amount = Number(((amount * firstPeriodRate * period) / 100 + commission).toFixed(2));
    data[0].interest = firstPeriodRate;
  }

  private initQueryParamsSubscription(): void {
    this.activatedRoute.queryParams
      .pipe(
        skip(1),
        tap((params) => this.handleQueryParams(params)),
        take(1),
      )
      .subscribe();
  }

  private handleQueryParams(params: Params): void {
    this.defaultKeys.forEach((key) => {
      if (params[key]) {
        this.sessionStorage.setItem(key, params[key]);
      }
    });

    const amount = +this.sessionStorage.getItem('amount');
    const term = +this.sessionStorage.getItem('term');
    if (amount && term) {
      this.updatePreviewParam(amount, term, this.getProduct().interest, true);
    }
  }

  private transformToScheduleData(data: IExpectedPayment[], dateFormatUI: string): IScheduleEvent[] {
    return data.map((ep: IExpectedPayment) => ({
      date: ep.date.format(dateFormatUI),
      amount: ep.amount,
      rate: ep.interest,
    }));
  }
}
